/**
 * Story mode on the field (docs/spellweave-modes.md §1): braziers (burning or
 * not), rune stones (glowing when claimed), doors and the portcullis (shut,
 * or sinking into the floor as they open), ice over frozen deep water,
 * brambles (standing, wet, burning, ash), molten rock and stone in the
 * chasm, the boiler with its gauge, and the Book's captions. Each uses the art library's still when it has landed (the
 * cloister set from games-media: same pixel look and ground projection as
 * the arena plate) and a simple drawn stand-in until then.
 */

import type { Arena, DoorArea, LevelObject, StorySnap, TerrainSnap } from '@shared/index.js';
import type { ViewEnemy } from '../state/viewModel.js';
import { ELEMENT_SWATCH, GROUND, UI, rgba } from '../palette.js';
import { TAU, UNIT, drawGlow, drawShadow, font, roundRectPath, type Ctx } from './gfx.js';
import type { Camera } from './camera.js';
import { art } from './sprites.js';

/** Brazier bowl rim height in world units (where the flames start). */
const BRAZIER_H = 1.1;
const STONE_H = 1.6;
const DOOR_H = 1.6;
/** Seconds a door takes to sink into the floor. */
export const DOOR_SINK_S = 0.7;

/** Draw a manifest still anchored at world (ax, ay); false until it has landed. */
function drawStill(ctx: Ctx, cam: Camera, name: string, ax: number, ay: number, clipBottom = 0): boolean {
  const im = art.image(name);
  if (!im || !im.origin || !im.pxPerUnit) return false;
  const w = (im.img.width / im.pxPerUnit) * UNIT;
  const h = (im.img.height / im.pxPerUnit) * UNIT;
  const x = cam.sx(ax + im.origin[0]);
  const y = cam.sy(ay + im.origin[1]);
  const smooth = ctx.imageSmoothingEnabled;
  ctx.imageSmoothingEnabled = false;
  if (clipBottom > 0) {
    // sinking: draw it lower and cut it off at its footing line
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.drawImage(im.img, x, y + clipBottom, w, h);
    ctx.restore();
  } else {
    ctx.drawImage(im.img, x, y, w, h);
  }
  ctx.imageSmoothingEnabled = smooth;
  return true;
}

// ------------------------------------------------------------------ braziers

export function drawBrazierGround(ctx: Ctx, cam: Camera, e: ViewEnemy): void {
  drawShadow(ctx, cam.sx(e.rx), cam.sy(e.ry), 0.55 * UNIT, 0.25 * UNIT, 0.5);
  if (e.lit) drawGlow(ctx, ELEMENT_SWATCH.fire.core, cam.sx(e.rx), cam.sy(e.ry), 3.4 * UNIT);
}

export function drawBrazier(ctx: Ctx, cam: Camera, e: ViewEnemy, time: number): void {
  if (e.oi === 'everflame') { drawEverflame(ctx, cam, e, time); return; }
  const px = cam.sx(e.rx), py = cam.sy(e.ry);
  const u = UNIT;
  if (!drawStill(ctx, cam, 'brazier', e.rx, e.ry)) {
    // stand-in: a stone plinth and an iron bowl
    ctx.fillStyle = GROUND.pillarFace;
    ctx.fillRect(px - 0.38 * u, py - 0.75 * u, 0.76 * u, 0.75 * u);
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.fillRect(px + 0.15 * u, py - 0.75 * u, 0.23 * u, 0.75 * u);
    ctx.fillStyle = '#2C2C33';
    ctx.beginPath();
    ctx.ellipse(px, py - BRAZIER_H * u + 0.12 * u, 0.5 * u, 0.2 * u, 0, 0, Math.PI);
    ctx.lineTo(px - 0.5 * u, py - BRAZIER_H * u + 0.12 * u);
    ctx.fill();
    ctx.fillStyle = '#4A4A55';
    ctx.beginPath();
    ctx.ellipse(px, py - BRAZIER_H * u + 0.1 * u, 0.5 * u, 0.18 * u, 0, 0, TAU);
    ctx.fill();
    ctx.fillStyle = e.lit ? '#FF8A3A' : '#1B1B20';
    ctx.beginPath();
    ctx.ellipse(px, py - BRAZIER_H * u + 0.1 * u, 0.38 * u, 0.12 * u, 0, 0, TAU);
    ctx.fill();
  }
  if (e.lit) drawFlames(ctx, px, py - BRAZIER_H * u, u, time, e.id);
}

/** The Everflame's bowl rim in world units above its base (the still's fire mark). */
const EVERFLAME_H = 1.56;

/** The Everflame: the big brazier behind Mother Mire, its still (or a wide stone bowl) and a fire to match. */
function drawEverflame(ctx: Ctx, cam: Camera, e: ViewEnemy, time: number): void {
  const px = cam.sx(e.rx), py = cam.sy(e.ry);
  const u = UNIT;
  if (e.lit) drawGlow(ctx, ELEMENT_SWATCH.fire.core, px, py, 6 * u);
  if (!drawStill(ctx, cam, 'everflame', e.rx, e.ry)) {
    ctx.fillStyle = GROUND.pillarFace;
    ctx.fillRect(px - 0.5 * u, py - 1.1 * u, 1.0 * u, 1.1 * u);
    ctx.fillStyle = '#6B5A3A';
    ctx.beginPath();
    ctx.ellipse(px, py - EVERFLAME_H * u + 0.15 * u, 0.85 * u, 0.32 * u, 0, 0, Math.PI);
    ctx.lineTo(px - 0.85 * u, py - EVERFLAME_H * u + 0.15 * u);
    ctx.fill();
    ctx.fillStyle = '#8A7448';
    ctx.beginPath();
    ctx.ellipse(px, py - EVERFLAME_H * u + 0.12 * u, 0.85 * u, 0.28 * u, 0, 0, TAU);
    ctx.fill();
    ctx.fillStyle = e.lit ? '#FF8A3A' : '#1B1B20';
    ctx.beginPath();
    ctx.ellipse(px, py - EVERFLAME_H * u + 0.12 * u, 0.66 * u, 0.2 * u, 0, 0, TAU);
    ctx.fill();
  }
  if (e.lit) drawFlames(ctx, px, py - EVERFLAME_H * u, u * 1.9, time, e.id);
}

/** A few flickering tongues of fire (no particles needed; it reads at any zoom). */
function drawFlames(ctx: Ctx, x: number, y: number, u: number, time: number, seed: number): void {
  const fire = ELEMENT_SWATCH.fire;
  drawGlow(ctx, fire.core, x, y - 0.3 * u, 2.2 * u);
  for (let i = 0; i < 5; i++) {
    const ph = time * (7 + i) + seed * 1.7 + i * 2.1;
    const fx = x + (i - 2) * 0.13 * u + Math.sin(ph) * 0.04 * u;
    const h = (0.55 + 0.25 * Math.sin(ph * 1.3) + (i === 2 ? 0.25 : 0)) * u;
    const w = (0.16 + 0.04 * Math.sin(ph * 0.7)) * u;
    ctx.fillStyle = i % 2 === 0 ? fire.core : fire.soft;
    ctx.beginPath();
    ctx.moveTo(fx - w, y);
    ctx.quadraticCurveTo(fx - w * 0.6, y - h * 0.55, fx, y - h);
    ctx.quadraticCurveTo(fx + w * 0.6, y - h * 0.55, fx + w, y);
    ctx.closePath();
    ctx.fill();
  }
  ctx.fillStyle = 'rgba(255,240,200,0.85)';
  ctx.beginPath();
  ctx.ellipse(x, y - 0.12 * u, 0.14 * u, 0.2 * u, 0, 0, TAU);
  ctx.fill();
}

// ------------------------------------------------------------------ rune stones

export function drawRuneStone(ctx: Ctx, cam: Camera, o: Extract<LevelObject, { k: 'rune-stone' }>, active: boolean, time: number): void {
  const px = cam.sx(o.x), py = cam.sy(o.y);
  const u = UNIT;
  drawShadow(ctx, px, py, 0.5 * u, 0.22 * u, 0.5);
  const pulse = 0.6 + 0.4 * Math.sin(time * 2.4);
  if (active) drawGlow(ctx, ELEMENT_SWATCH.arcane.soft, px, py - 0.8 * u, (2.6 + pulse * 0.6) * u);
  if (!drawStill(ctx, cam, 'rune-stone', o.x, o.y)) {
    ctx.fillStyle = '#5E6577';
    ctx.beginPath();
    ctx.moveTo(px - 0.4 * u, py);
    ctx.lineTo(px - 0.34 * u, py - STONE_H * u + 0.2 * u);
    ctx.quadraticCurveTo(px, py - STONE_H * u - 0.1 * u, px + 0.34 * u, py - STONE_H * u + 0.2 * u);
    ctx.lineTo(px + 0.4 * u, py);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.fillRect(px + 0.16 * u, py - STONE_H * u + 0.25 * u, 0.22 * u, STONE_H * u - 0.25 * u);
  }
  // the runes: dim carvings, lit when this is the stone you'll stand up at
  ctx.strokeStyle = active ? rgba('#BFE3FF', 0.6 + 0.4 * pulse) : 'rgba(20,24,34,0.65)';
  ctx.lineWidth = Math.max(1, 0.06 * u);
  ctx.beginPath();
  const ry = py - STONE_H * u * 0.62;
  ctx.moveTo(px - 0.12 * u, ry - 0.3 * u); ctx.lineTo(px + 0.12 * u, ry - 0.1 * u); ctx.lineTo(px - 0.1 * u, ry + 0.1 * u);
  ctx.moveTo(px, ry + 0.2 * u); ctx.lineTo(px, ry + 0.45 * u);
  ctx.stroke();
}

// ------------------------------------------------------------------ doors

/** A shut door (or one still sinking, `sink` 0..1). Anchored at the footprint's top-left. */
export function drawDoor(ctx: Ctx, cam: Camera, d: DoorArea, sink: number, time: number): void {
  const u = UNIT;
  const r = d.rect;
  const drop = sink * DOOR_H * u;
  if (drop >= DOOR_H * u) return;
  if (d.art && drawStill(ctx, cam, d.art, r.x, r.y, drop)) return;
  // stand-in: a slab filling the opening, its south face up from the footing
  const x0 = cam.sx(r.x), x1 = cam.sx(r.x + r.w);
  const top = cam.sy(r.y) - DOOR_H * u + drop;
  const foot = cam.sy(r.y + r.h);
  ctx.save();
  ctx.beginPath();
  ctx.rect(x0, cam.sy(r.y) - DOOR_H * u, x1 - x0, foot - cam.sy(r.y) + DOOR_H * u);
  ctx.clip();
  ctx.fillStyle = GROUND.wallTop;
  ctx.fillRect(x0, top, x1 - x0, foot - DOOR_H * u - top + drop);
  ctx.fillStyle = GROUND.wallFace;
  ctx.fillRect(x0, foot - DOOR_H * u + drop, x1 - x0, DOOR_H * u - drop);
  if (d.art === 'portcullis') {
    // iron bars over the opening
    ctx.fillStyle = '#1A1C22';
    ctx.fillRect(x0, top, x1 - x0, foot - top);
    ctx.fillStyle = '#5C6170';
    const bars = Math.max(3, Math.round((x1 - x0) / (0.4 * u)));
    for (let i = 0; i <= bars; i++) ctx.fillRect(x0 + ((x1 - x0 - 3) * i) / bars, top, 3, foot - top);
    for (let k = 1; k <= 3; k++) ctx.fillRect(x0, top + ((foot - top) * k) / 4, x1 - x0, 3);
  }
  if (d.art === 'rune-door') {
    const glow = 0.45 + 0.25 * Math.sin(time * 1.8);
    ctx.strokeStyle = rgba('#FFB463', glow);
    ctx.lineWidth = Math.max(1, 0.07 * u);
    const cx = (x0 + x1) / 2, cy = (top + foot - DOOR_H * u + drop) / 2;
    ctx.beginPath();
    ctx.arc(cx, cy, 0.45 * u, 0, TAU);
    ctx.moveTo(cx, cy - 0.45 * u); ctx.lineTo(cx, cy + 0.45 * u);
    ctx.stroke();
  }
  ctx.restore();
}

// ------------------------------------------------------------------ ice

/** Ice over the frozen deep-water cells: pale blue, a little shine, cracks as it nears thawing. */
export function drawIce(ctx: Ctx, cam: Camera, arena: Arena, frozen: readonly number[], time: number): void {
  if (frozen.length === 0) return;
  const u = UNIT;
  const w = arena.w;
  for (const cell of frozen) {
    const cx = cell % w, cy = Math.floor(cell / w);
    const x = cam.sx(cx), y = cam.sy(cy);
    ctx.fillStyle = 'rgba(196,232,255,0.82)';
    ctx.fillRect(x, y, u + 0.5, u + 0.5);
    ctx.fillStyle = 'rgba(255,255,255,0.22)';
    ctx.fillRect(x + u * 0.1, y + u * 0.15, u * 0.5, u * 0.08);
    if (((cell * 7 + Math.floor(time * 0.5)) % 5) === 0) {
      ctx.strokeStyle = 'rgba(120,170,210,0.6)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x + u * 0.2, y + u * 0.7);
      ctx.lineTo(x + u * 0.5, y + u * 0.45);
      ctx.lineTo(x + u * 0.8, y + u * 0.6);
      ctx.stroke();
    }
  }
}

// ------------------------------------------------------------------ Mother Mire's flood

/**
 * A flooded room (Mother Mire): its floor under a sheet of moving water,
 * boiling (bubbles and steam) once the Everflame burns. The flood's water
 * boxes are in the map's water (level.ts withTerrain).
 */
export function drawFlood(ctx: Ctx, cam: Camera, arena: Arena, tr: TerrainSnap | null, time: number): void {
  if (!tr?.f || tr.f.length === 0) return;
  const u = UNIT;
  for (const id of tr.f) {
    const room = arena.rooms.find((r) => r.id === id);
    if (!room) continue;
    const r = room.rect;
    for (const z of arena.water) {
      if (z.k !== 'rect' || z.x < r.x || z.y < r.y || z.x + z.w > r.x + r.w || z.y + z.h > r.y + r.h) continue;
      const x = cam.sx(z.x), y = cam.sy(z.y), w = z.w * u, h = z.h * u;
      ctx.fillStyle = tr.bo ? 'rgba(120,150,170,0.38)' : 'rgba(40,110,190,0.34)';
      ctx.fillRect(x, y, w, h);
      // slow light bands drifting across the water
      ctx.strokeStyle = 'rgba(190,225,255,0.18)';
      ctx.lineWidth = Math.max(1, 0.06 * u);
      ctx.beginPath();
      for (let gy = Math.ceil(z.y); gy < z.y + z.h; gy += 2) {
        const off = ((time * 0.6 + gy * 0.37) % 2) * u;
        for (let gx = z.x; gx < z.x + z.w - 1; gx += 3) {
          const sx = cam.sx(gx) + off, sy = cam.sy(gy) + Math.sin(time * 1.4 + gx) * 0.08 * u;
          ctx.moveTo(sx, sy);
          ctx.lineTo(sx + 0.9 * u, sy);
        }
      }
      ctx.stroke();
      if (!tr.bo) continue;
      // boiling: bubbles popping and steam rising
      for (let gy = Math.ceil(z.y); gy < z.y + z.h; gy++) {
        for (let gx = Math.ceil(z.x); gx < z.x + z.w; gx++) {
          const n = cellNoise(gy * arena.w + gx, 3);
          if (n > 0.3) continue;
          const ph = (time * (0.8 + n) + n * 7) % 1;
          const bx = cam.sx(gx + 0.5), by = cam.sy(gy + 0.5);
          ctx.fillStyle = `rgba(235,240,245,${(0.5 * (1 - ph)).toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(bx, by - ph * 0.9 * u, (0.08 + ph * 0.25) * u, 0, TAU);
          ctx.fill();
        }
      }
    }
  }
}

// ------------------------------------------------------------------ the Book's captions

interface Caption { text: string; t: number; dur: number }

/** The Book speaks in one line at a time near the bottom of the screen; lines queue up. */
export class Captions {
  private readonly queue: Caption[] = [];

  say(text: string): void {
    // ~14 characters a second to read, 2.6 s at least
    this.queue.push({ text, t: 0, dur: Math.max(2.6, 0.9 + text.length / 14) });
    if (this.queue.length > 6) this.queue.splice(0, this.queue.length - 6);
  }

  clear(): void {
    this.queue.length = 0;
  }

  step(dt: number): void {
    const c = this.queue[0];
    if (!c) return;
    c.t += dt;
    if (c.t >= c.dur) this.queue.shift();
  }

  /** `y` is the line's centre; `maxW` the widest it may be (phone screens wrap nothing, they shrink). */
  draw(ctx: Ctx, cx: number, y: number, maxW: number, s: number): void {
    const c = this.queue[0];
    if (!c) return;
    const fade = Math.min(1, c.t / 0.25, (c.dur - c.t) / 0.4);
    let size = 15 * s;
    ctx.font = font(size, 600);
    let tw = ctx.measureText(c.text).width;
    if (tw > maxW - 40 * s) {
      size *= (maxW - 40 * s) / tw;
      ctx.font = font(size, 600);
      tw = ctx.measureText(c.text).width;
    }
    const pw = tw + 64 * s, ph = size + 18 * s;
    ctx.globalAlpha = Math.max(0, fade);
    ctx.fillStyle = 'rgba(10,12,18,0.78)';
    roundRectPath(ctx, cx - pw / 2, y - ph / 2, pw, ph, 8 * s);
    ctx.fill();
    ctx.fillStyle = UI.gold;
    ctx.font = font(11 * s, 800);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('BOOK', cx - pw / 2 + 12 * s, y);
    ctx.font = font(size, 600);
    ctx.fillStyle = UI.text;
    ctx.textAlign = 'center';
    ctx.fillText(c.text, cx + 16 * s, y);
    ctx.globalAlpha = 1;
  }
}

/** Is this the rune stone the party stands up at? */
export function stoneActive(story: StorySnap | null, id: string): boolean {
  return story !== null && story.c === id;
}

// ------------------------------------------------------------------ brambles and the chasm (slice 3)

/** Visible cell range of the camera, clamped to the map. */
function viewCells(cam: Camera, a: Arena): { x0: number; x1: number; y0: number; y1: number } {
  return {
    x0: Math.max(0, Math.floor(cam.left) - 1),
    x1: Math.min(a.w - 1, Math.ceil(cam.right) + 1),
    y0: Math.max(0, Math.floor(cam.top) - 1),
    y1: Math.min(a.h - 1, Math.ceil(cam.bottom) + 2),
  };
}

/** A still drawn over one 1 u cell (its origin is the cell's top-left); false until it has landed. */
function drawCellStill(ctx: Ctx, cam: Camera, name: string, x: number, y: number): boolean {
  return drawStill(ctx, cam, name, x, y);
}

/** Small deterministic noise per cell (stand-in thorns, flame phases). */
function cellNoise(cell: number, k: number): number {
  const s = Math.sin(cell * 12.9898 + k * 78.233) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * Brambles: standing ones (dark thorny tiles; wet ones glisten, burning ones
 * flicker with flame), and ash where they burnt away. Drawn on the ground
 * layer, row by row, so a tile's top overlaps the row above.
 */
export function drawBrambles(ctx: Ctx, cam: Camera, arena: Arena, tr: TerrainSnap | null, time: number): void {
  if (arena.brambleCells.length === 0) return;
  const ash = new Set(tr?.a ?? []);
  const burning = new Set(tr?.b ?? []);
  const wet = new Set(tr?.wt ?? []);
  const v = viewCells(cam, arena);
  const u = UNIT;
  const w = arena.w;
  for (const cell of arena.brambleCells) {
    const x = cell % w, y = Math.floor(cell / w);
    if (x < v.x0 || x > v.x1 || y < v.y0 || y > v.y1) continue;
    const sx = cam.sx(x), sy = cam.sy(y);
    if (ash.has(cell)) {
      if (!drawCellStill(ctx, cam, 'bramble-ash', x, y)) {
        ctx.fillStyle = 'rgba(40,36,34,0.9)';
        ctx.fillRect(sx, sy, u + 0.5, u + 0.5);
        ctx.fillStyle = 'rgba(120,112,104,0.35)';
        ctx.fillRect(sx + u * cellNoise(cell, 1) * 0.6, sy + u * cellNoise(cell, 2) * 0.6, u * 0.3, u * 0.18);
      }
      continue;
    }
    if (!drawCellStill(ctx, cam, 'bramble', x, y)) {
      ctx.fillStyle = '#1E2A1A';
      ctx.fillRect(sx, sy - u * 0.25, u + 0.5, u * 1.25 + 0.5);
      ctx.strokeStyle = '#3F5A2E';
      ctx.lineWidth = Math.max(1, u * 0.06);
      ctx.beginPath();
      for (let i = 0; i < 3; i++) {
        const ax = sx + u * (0.15 + 0.3 * i + 0.1 * cellNoise(cell, i));
        ctx.moveTo(ax, sy + u);
        ctx.quadraticCurveTo(ax + u * 0.25, sy + u * 0.4, ax + u * (0.1 + 0.2 * cellNoise(cell, i + 3)), sy - u * 0.2);
      }
      ctx.stroke();
      ctx.fillStyle = '#6E7A4A';
      ctx.fillRect(sx + u * cellNoise(cell, 7) * 0.8, sy + u * cellNoise(cell, 8) * 0.8, 2, 2);
    }
    if (wet.has(cell)) {
      ctx.fillStyle = rgba(ELEMENT_SWATCH.water.core, 0.22);
      ctx.fillRect(sx, sy - u * 0.25, u + 0.5, u * 1.25 + 0.5);
      if (((cell + Math.floor(time * 2)) % 4) === 0) {
        ctx.fillStyle = 'rgba(210,235,255,0.7)';
        ctx.fillRect(sx + u * cellNoise(cell, 9) * 0.8, sy + u * cellNoise(cell, 10) * 0.7, 2, 2);
      }
    }
  }
  // flames go over every tile, so draw them last
  for (const cell of burning) {
    const x = cell % w, y = Math.floor(cell / w);
    if (x < v.x0 || x > v.x1 || y < v.y0 || y > v.y1) continue;
    const cx = cam.sx(x + 0.5), cy = cam.sy(y + 0.9);
    drawGlow(ctx, ELEMENT_SWATCH.fire.core, cx, cy - u * 0.3, 2.4 * u);
    smallFlames(ctx, cx, cy, u * 0.8, time, cell);
  }
}

/** A cell-sized fire: three tongues. */
function smallFlames(ctx: Ctx, x: number, y: number, u: number, time: number, seed: number): void {
  const fire = ELEMENT_SWATCH.fire;
  for (let i = 0; i < 3; i++) {
    const ph = time * (8 + i) + seed * 0.37 + i * 2.1;
    const fx = x + (i - 1) * 0.22 * u + Math.sin(ph) * 0.05 * u;
    const h = (0.6 + 0.3 * Math.sin(ph * 1.3) + (i === 1 ? 0.3 : 0)) * u;
    const fw = 0.16 * u;
    ctx.fillStyle = i === 1 ? fire.core : fire.soft;
    ctx.beginPath();
    ctx.moveTo(fx - fw, y);
    ctx.quadraticCurveTo(fx - fw * 0.6, y - h * 0.55, fx, y - h);
    ctx.quadraticCurveTo(fx + fw * 0.6, y - h * 0.55, fx + fw, y);
    ctx.closePath();
    ctx.fill();
  }
}

/** The chasm's fills: molten rock (glowing, pulsing) and stone set from it (a walkable slab). */
export function drawChasmFills(ctx: Ctx, cam: Camera, arena: Arena, tr: TerrainSnap | null, time: number): void {
  if (!tr || (!tr.m && !tr.s)) return;
  const u = UNIT;
  const w = arena.w;
  const v = viewCells(cam, arena);
  for (const cell of tr.s ?? []) {
    const x = cell % w, y = Math.floor(cell / w);
    if (x < v.x0 || x > v.x1 || y < v.y0 || y > v.y1) continue;
    if (drawCellStill(ctx, cam, 'stone-fill', x, y)) continue;
    const sx = cam.sx(x), sy = cam.sy(y);
    ctx.fillStyle = '#3A3634';
    ctx.fillRect(sx, sy, u + 0.5, u + 0.5);
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.fillRect(sx, sy + u * 0.85, u + 0.5, u * 0.15);
    ctx.fillStyle = 'rgba(255,170,90,0.12)';
    ctx.fillRect(sx + u * 0.2, sy + u * 0.3, u * 0.5, u * 0.08);
  }
  for (const cell of tr.m ?? []) {
    const x = cell % w, y = Math.floor(cell / w);
    if (x < v.x0 || x > v.x1 || y < v.y0 || y > v.y1) continue;
    const sx = cam.sx(x), sy = cam.sy(y);
    if (!drawCellStill(ctx, cam, 'lava-fill', x, y)) {
      ctx.fillStyle = '#B8401A';
      ctx.fillRect(sx, sy, u + 0.5, u + 0.5);
      ctx.fillStyle = '#FF9A3A';
      const p = 0.5 + 0.5 * Math.sin(time * 3 + cell * 0.7);
      ctx.fillRect(sx + u * 0.15, sy + u * (0.2 + 0.2 * p), u * 0.7, u * 0.12);
    }
    const pulse = 0.75 + 0.25 * Math.sin(time * 2.2 + cell);
    drawGlow(ctx, ELEMENT_SWATCH.fire.core, sx + u * 0.5, sy + u * 0.5, 1.6 * u * pulse);
  }
}

// ------------------------------------------------------------------ the boiler

/**
 * Where the game draws over the boiler, in world units from its anchor (y
 * down): the gauge dial, the fire door, the sight glass and the chimney top.
 * `art` matches the factory still's marks (games-media library/stills/boiler);
 * `standIn` the drawn drum used until it lands.
 */
interface BoilerMarks { gauge: { x: number; y: number; r: number }; fire: { x: number; y: number; w: number; h: number }; glass: { x: number; y: number; h: number }; steam: { x: number; y: number } }
const BOILER_MARKS: { art: BoilerMarks; standIn: BoilerMarks } = {
  art: { gauge: { x: 0, y: -0.474, r: 0.19 }, fire: { x: 0, y: 0.76, w: 0.68, h: 0.4 }, glass: { x: 1.0, y: -1.7, h: 1.2 }, steam: { x: 0.28, y: -4.0 } },
  standIn: { gauge: { x: -0.2, y: -1.45, r: 0.32 }, fire: { x: 0, y: -0.47, w: 0.64, h: 0.42 }, glass: { x: 0.75, y: -1.9, h: 1.1 }, steam: { x: 0, y: -2.6 } },
};

export function drawBoilerGround(ctx: Ctx, cam: Camera, e: ViewEnemy): void {
  const heat = e.os?.[1] ?? 0;
  drawShadow(ctx, cam.sx(e.rx), cam.sy(e.ry), 1.25 * UNIT, 0.5 * UNIT, 0.5);
  if (heat > 0.05) drawGlow(ctx, ELEMENT_SWATCH.fire.core, cam.sx(e.rx), cam.sy(e.ry), (2 + 3 * heat) * UNIT);
}

/** The boiler: the still (or a riveted copper drum), its fire door glowing with heat, a sight glass while it fills, the gauge needle on pressure, steam once it is hot. */
export function drawBoiler(ctx: Ctx, cam: Camera, e: ViewEnemy, time: number): void {
  const [water = 0, heat = 0, pressure = 0] = e.os ?? [];
  const u = UNIT;
  const px = cam.sx(e.rx), py = cam.sy(e.ry);
  const art = drawStill(ctx, cam, 'boiler', e.rx, e.ry);
  const mk = art ? BOILER_MARKS.art : BOILER_MARKS.standIn;
  if (!art) {
    // stand-in: a copper drum, a firebox at the front
    ctx.fillStyle = '#5A3A22';
    ctx.fillRect(px - 1.1 * u, py - 2.2 * u, 2.2 * u, 2.0 * u);
    ctx.fillStyle = '#8A5A32';
    ctx.fillRect(px - 1.0 * u, py - 2.2 * u, 0.5 * u, 2.0 * u);
    ctx.fillStyle = '#3B2616';
    ctx.beginPath();
    ctx.ellipse(px, py - 2.2 * u, 1.1 * u, 0.4 * u, 0, 0, TAU);
    ctx.fill();
    ctx.fillStyle = '#C8925A';
    for (let i = 0; i < 6; i++) ctx.fillRect(px - 1.0 * u + i * 0.38 * u, py - 1.95 * u, 2, 2);
    ctx.fillStyle = '#1B1B20';
    ctx.fillRect(px - 0.4 * u, py - 0.75 * u, 0.8 * u, 0.55 * u);
    // the dial's face (the still has its own)
    ctx.fillStyle = '#E8DFC8';
    ctx.beginPath();
    ctx.arc(px + mk.gauge.x * u, py + mk.gauge.y * u, mk.gauge.r * u, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = '#2C2C33';
    ctx.lineWidth = Math.max(1, 0.05 * u);
    ctx.stroke();
  }
  // the fire door glows with heat
  if (heat > 0.02) {
    const f = mk.fire;
    ctx.fillStyle = rgba('#FF8A3A', Math.min(0.9, 0.25 + heat * 0.7));
    ctx.fillRect(px + (f.x - f.w / 2) * u, py + (f.y - f.h / 2) * u, f.w * u, f.h * u);
    drawGlow(ctx, ELEMENT_SWATCH.fire.core, px + f.x * u, py + f.y * u, (0.8 + heat * 1.4) * u);
  }
  // water level: a sight glass on the side, until it is full
  if (water < 1 || !art) {
    const g = mk.glass;
    const gx = px + g.x * u, gy0 = py + g.y * u, gh = g.h * u;
    ctx.fillStyle = 'rgba(10,12,18,0.85)';
    ctx.fillRect(gx - 0.08 * u, gy0, 0.16 * u, gh);
    ctx.fillStyle = rgba(ELEMENT_SWATCH.water.core, 0.95);
    ctx.fillRect(gx - 0.06 * u, gy0 + gh * (1 - water), 0.12 * u, gh * water);
  }
  // the gauge needle sweeps with the pressure, past a red mark at the top of its travel
  const cx = px + mk.gauge.x * u, cy = py + mk.gauge.y * u, r = mk.gauge.r * u;
  ctx.lineWidth = Math.max(1, 0.05 * u);
  ctx.strokeStyle = '#C0392B';
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.78, Math.PI * 0.25 - 0.4, Math.PI * 0.25, false);
  ctx.stroke();
  const ang = Math.PI * 0.75 + pressure * Math.PI * 1.5;
  ctx.strokeStyle = '#141418';
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + Math.cos(ang) * r * 0.85, cy + Math.sin(ang) * r * 0.85);
  ctx.stroke();
  // hot: steam from the chimney
  if (water >= 1 && heat >= 0.6) {
    const sx = px + mk.steam.x * u, sy = py + mk.steam.y * u;
    for (let i = 0; i < 3; i++) {
      const f = (time * 0.6 + i / 3) % 1;
      ctx.fillStyle = `rgba(230,235,240,${(0.45 * (1 - f)).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(sx + Math.sin(time * 1.3 + i * 2) * 0.25 * u, sy - f * 1.4 * u, (0.18 + f * 0.35) * u, 0, TAU);
      ctx.fill();
    }
  }
}
